import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Eye, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [labelForm, setLabelForm] = useState({ label: "", label_en: "" });

  const startEdit = (item) => {
    setEditingId(item.id);
    setLabelForm({ label: item.label, label_en: item.label_en || "" });
  };

  const saveLabels = async (item) => {
    try {
      const res = await api.put(`/admin/menu/${item.id}`, labelForm);
      setItems(items.map((x) => (x.id === item.id ? res.data : x)));
      setEditingId(null);
      toast.success("Etichette aggiornate. / Labels updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const refresh = () =>
    api.get("/admin/menu").then((res) => setItems(res.data)).catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const moveItem = async (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    try {
      await api.post("/admin/menu/reorder", { order: next.map((x) => x.id) });
      toast.success("Ordine del menu aggiornato. / Menu order updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      refresh();
    }
  };

  const toggleVisible = async (item) => {
    try {
      const res = await api.put(`/admin/menu/${item.id}`, { visible: !item.visible });
      setItems(items.map((x) => (x.id === item.id ? res.data : x)));
      toast.success(res.data.visible ? "Voce mostrata nel menu. / Shown." : "Voce nascosta dal menu. / Hidden.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="mt-8 space-y-3">
        <p className="text-xs font-light text-slate-400">
          Trascina per riordinare le voci del menu. Nascondi una voce per toglierla dal menu: la pagina resta raggiungibile ma viene esclusa anche da Google (noindex e fuori dalla sitemap).
          <br />
          Drag to reorder the navigation menu. Hiding an entry removes it from the menu and from search engines (noindex, out of the sitemap). The page stays reachable.
        </p>
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`admin-menu-row-${item.key}`}
            draggable
            onDragStart={(e) => {
              setDragIndex(items.findIndex((x) => x.id === item.id));
              e.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              moveItem(
                dragIndex,
                items.findIndex((x) => x.id === item.id)
              );
            }}
            onDragEnd={() => setDragIndex(null)}
            className={`flex items-center justify-between gap-4 bg-white border border-slate-200 p-5 cursor-grab active:cursor-grabbing ${
              dragIndex === items.findIndex((x) => x.id === item.id) ? "opacity-50" : ""
            }`}
          >
            <div className="min-w-0 flex-1">
              {editingId === item.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    data-testid={`admin-menu-label-it-${item.key}`}
                    value={labelForm.label}
                    onChange={(e) => setLabelForm({ ...labelForm, label: e.target.value })}
                    placeholder="Etichetta (IT)"
                    className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand"
                  />
                  <input
                    data-testid={`admin-menu-label-en-${item.key}`}
                    value={labelForm.label_en}
                    onChange={(e) => setLabelForm({ ...labelForm, label_en: e.target.value })}
                    placeholder="Etichetta (EN)"
                    className="w-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand"
                  />
                </div>
              ) : (
                <>
                  <p className="font-serif text-lg text-slate-900">
                    {item.label}
                    {item.label_en && item.label_en !== item.label && (
                      <span className="ml-2 text-sm font-light text-slate-400">/ {item.label_en}</span>
                    )}
                  </p>
                  <p className="text-xs font-light text-slate-400">{item.path}</p>
                </>
              )}
            </div>
            <div className="flex gap-2 shrink-0 items-center">
              {editingId === item.id ? (
                <>
                  <Button
                    size="sm"
                    data-testid={`admin-menu-save-${item.key}`}
                    onClick={() => saveLabels(item)}
                    className="bg-brand hover:bg-brand-dark"
                  >
                    Salva / Save
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`admin-menu-cancel-edit-${item.key}`} onClick={() => setEditingId(null)}>
                    Annulla
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid={`admin-menu-edit-${item.key}`}
                    onClick={() => startEdit(item)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <button
                    data-testid={`admin-menu-toggle-${item.key}`}
                    onClick={() => toggleVisible(item)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 border ${
                      item.visible
                        ? "border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600"
                        : "bg-brand text-white border-brand hover:bg-brand-dark"
                    }`}
                  >
                    {item.visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    {item.visible ? "Nascondi / Hide" : "Mostra / Show"}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm font-light text-slate-400">Nessuna voce / No entries.</p>
        )}
      </div>
    </div>
  );
}
